import { FC, useState, useCallback } from 'react'
import {
  PanelLeftClose,
  Plus,
  Settings,
  ChevronDown,
  FolderOpen,
} from 'lucide-react'
import { Button } from '../ui/button'
import { SidebarContent } from './SidebarContent'
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip'
import { cn } from '../../lib/utils'
import { useWorkspace } from '../../contexts/WorkspaceContext'
import { useChat } from '../../contexts/ChatContext'
import { workspaceService } from '../../services/workspaceService'
import type { WorkspaceType } from '../../services/workspaceService'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../ui/dropdown-menu'

interface SidebarProps {
  isOpen: boolean
  onToggle: () => void
  userName?: string
  userInitial?: string
  onOpenSettings?: () => void
  onOpenManager?: () => void
  /** Fired after a navigation action (switch/create). On mobile, closes the drawer. */
  onNavigate?: () => void
}

const typeOptions: Array<{ type: WorkspaceType; label: string; description: string }> = [
  { type: 'admission', label: 'Admission', description: 'Full admission workup' },
  { type: 'flow', label: 'Flow', description: 'Quick patient flow' },
  { type: 'chat', label: 'Chat', description: 'General chat session' },
]

/**
 * Sidebar component -- workspace-backed.
 * Uses useWorkspace() context for all CRUD operations.
 * Groups workspaces by type with type badges, relative time, inline rename, and close.
 */
export const Sidebar: FC<SidebarProps> = ({
  isOpen: _isOpen,
  onToggle,
  userName,
  userInitial,
  onOpenSettings,
  onOpenManager,
  onNavigate,
}) => {
  const {
    workspaces,
    activeWorkspaceId,
    isLoading,
    createWorkspace,
    switchWorkspace,
    closeWorkspace,
    saveState,
    navigateToIntake,
  } = useWorkspace()

  const { removeWorkspaceConversation } = useChat()

  const [isCreating, setIsCreating] = useState(false)

  const handleNewWorkspace = useCallback(
    async (type: WorkspaceType) => {
      if (isCreating) return
      setIsCreating(true)
      onNavigate?.()
      try {
        if (type === 'chat') {
          await createWorkspace('chat')
        } else {
          await navigateToIntake(type)
        }
      } catch (err) {
        console.error('[Sidebar] Failed to handle new workspace:', err)
      } finally {
        setIsCreating(false)
      }
    },
    [createWorkspace, navigateToIntake, isCreating, onNavigate]
  )

  const handleRename = useCallback(
    (id: string, newName: string) => {
      // Optimistic rename via saveState (debounced to backend)
      // Also update immediately via direct API call for the sidebar display
      workspaceService.update(id, { display_name: newName }).catch(err => {
        console.error('[Sidebar] Failed to rename workspace:', err)
      })
      // If renaming the active workspace, also push through saveState
      if (id === activeWorkspaceId) {
        saveState({ display_name: newName })
      }
    },
    [activeWorkspaceId, saveState]
  )

  const handleClose = useCallback(
    async (id: string) => {
      try {
        await closeWorkspace(id)
      } catch (err) {
        console.error('[Sidebar] Failed to close workspace:', err)
      }
    },
    [closeWorkspace]
  )

  const handleDelete = useCallback(
    async (id: string) => {
      try {
        removeWorkspaceConversation(id)
        await closeWorkspace(id, true)
      } catch (err) {
        console.error('[Sidebar] Failed to delete workspace:', err)
      }
    },
    [closeWorkspace, removeWorkspaceConversation]
  )

  const handleSwitchWorkspace = useCallback(
    async (id: string) => {
      // Close the mobile drawer first so a single tap both navigates and dismisses;
      // the switch then resolves behind the (now-hidden) drawer.
      onNavigate?.()
      try {
        await switchWorkspace(id)
      } catch (err) {
        console.error('[Sidebar] Failed to switch workspace:', err)
      }
    },
    [switchWorkspace, onNavigate]
  )

  return (
    <div className="h-full flex flex-col bg-surface/50 backdrop-blur-sm">
      {/* Header: New Workspace + Collapse.
          On mobile the drawer supplies its own close (X), so reserve right padding
          to keep the New Workspace button clear of it and hide the collapse button. */}
      <div className="flex items-center gap-2 pl-3 pr-12 pt-3 pb-2 md:pr-3">
        {/* New Workspace dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              disabled={isCreating}
              className={cn(
                'flex-1 h-8 rounded-lg font-display font-semibold text-xs',
                'bg-primary text-primary-foreground',
                'hover:bg-primary/90',
                'transition-all duration-200'
              )}
            >
              <Plus className="mr-1.5 h-3.5 w-3.5" />
              New Workspace
              <ChevronDown className="ml-1 h-3 w-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="min-w-[180px]">
            {typeOptions.map(opt => (
              <DropdownMenuItem
                key={opt.type}
                onClick={() => handleNewWorkspace(opt.type)}
                className="flex flex-col items-start gap-0.5 py-2"
              >
                <span className="text-sm font-medium">{opt.label}</span>
                <span className="text-[10px] text-muted-foreground">{opt.description}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              onClick={onToggle}
              variant="ghost"
              size="icon"
              className={cn(
                'hidden md:flex',
                'h-8 w-8 rounded-lg flex-shrink-0',
                'bg-surface-elevated/80 backdrop-blur-sm',
                'border border-border/50 hover:border-primary/30',
                'hover:bg-surface-elevated',
                'transition-all duration-200'
              )}
            >
              <PanelLeftClose className="h-3.5 w-3.5 text-muted-foreground" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">
            <p className="text-xs">Hide sidebar (Ctrl+B)</p>
          </TooltipContent>
        </Tooltip>
      </div>

      {/* Workspace Manager button */}
      {onOpenManager && (
        <button
          onClick={onOpenManager}
          className={cn(
            'flex items-center gap-2 mx-3 mb-2 px-3 py-1.5 rounded-lg',
            'text-xs text-muted-foreground',
            'hover:bg-accent/50 hover:text-foreground',
            'transition-colors duration-150'
          )}
        >
          <FolderOpen className="h-3.5 w-3.5" />
          <span>Workspace Manager</span>
        </button>
      )}

      {/* Divider */}
      <div className="h-px bg-border/50 mx-3" />

      {/* Workspace list */}
      <SidebarContent
        workspaces={workspaces}
        activeWorkspaceId={activeWorkspaceId}
        onWorkspaceSelect={handleSwitchWorkspace}
        onRename={handleRename}
        onClose={handleClose}
        onDelete={handleDelete}
        isLoading={isLoading}
      />

      {/* User Profile Row */}
      {userName && onOpenSettings && (
        <div className="flex-shrink-0 mt-auto border-t border-border/50">
          <button
            onClick={onOpenSettings}
            aria-label={`${userName}, open settings and subscription`}
            className={cn(
              'w-full flex items-center gap-3 px-3 py-3',
              'hover:bg-accent/50 transition-colors duration-200',
              'group'
            )}
          >
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <span className="text-sm font-medium text-primary">
                {userInitial || userName[0]?.toUpperCase() || 'U'}
              </span>
            </div>
            <div className="flex-1 min-w-0 text-left">
              <p className="text-sm font-medium truncate">{userName}</p>
              <p className="text-[10px] text-muted-foreground">Settings</p>
            </div>
            <Settings className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
          </button>
        </div>
      )}
    </div>
  )
}
